import { createTheme } from '@mui/material/styles';
import {
  marketingColor,
  marketingFont,
  marketingLine,
  marketingRadius,
  marketingShadow,
  marketingSurface,
} from './marketingTokens';

/**
 * MUI theme for the public-facing marketing site (`PublicLayout` and every page
 * it wraps). The signed-in app keeps `appTheme`; this one is provided *inside*
 * it so the swap is scoped to the marketing routes.
 *
 * The grey ramp is deliberately inverted. Marketing pages use `grey.50`/`100`
 * for alternating section bands and `grey.800`/`900` for code panels, all as
 * backgrounds — mapping the ramp onto the dark surface scale re-themes those
 * bands without touching every page, and keeps the light/dark relationship
 * (a `grey.50` band still reads as "raised" against the page).
 */
export const marketingTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: marketingColor.accent,
      light: marketingColor.accentHover,
      dark: marketingColor.accentDeep,
      contrastText: marketingColor.ink,
    },
    secondary: {
      main: marketingColor.textSecondary,
      light: marketingColor.text,
      dark: marketingColor.textMuted,
      contrastText: marketingColor.ink,
    },
    background: {
      default: marketingColor.ink,
      paper: marketingColor.surface,
    },
    text: {
      primary: marketingColor.text,
      secondary: marketingColor.textSecondary,
      disabled: marketingColor.textMuted,
    },
    divider: marketingLine.hairline,
    success: {
      main: marketingColor.accent,
      contrastText: marketingColor.ink,
    },
    warning: {
      main: marketingColor.warning,
      contrastText: marketingColor.ink,
    },
    error: {
      main: marketingColor.error,
      contrastText: marketingColor.ink,
    },
    info: {
      main: marketingColor.accentHover,
      contrastText: marketingColor.ink,
    },
    // Inverted ramp — see the note above. Every step is a background value.
    grey: {
      50: marketingColor.surface,
      100: marketingColor.surface,
      200: marketingColor.surfaceSelected,
      300: marketingColor.surfaceSelected,
      400: marketingColor.surfaceStrong,
      500: marketingColor.surfaceStrong,
      600: marketingColor.surfaceSelected,
      700: marketingColor.surface,
      800: marketingColor.surface,
      900: marketingColor.ink,
    },
  },
  shape: {
    borderRadius: marketingRadius.md,
  },
  typography: {
    fontFamily: marketingFont.body,
    h1: {
      fontFamily: marketingFont.display,
      fontSize: '3.625rem',
      fontWeight: 700,
      lineHeight: 1.08,
      letterSpacing: '-0.01em',
    },
    h2: {
      fontFamily: marketingFont.display,
      fontSize: '2.75rem',
      fontWeight: 700,
      lineHeight: 1.12,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: marketingFont.display,
      fontSize: '2.125rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h4: {
      fontFamily: marketingFont.display,
      fontSize: '1.75rem',
      fontWeight: 700,
      lineHeight: 1.25,
    },
    h5: {
      fontFamily: marketingFont.display,
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h6: {
      fontFamily: marketingFont.display,
      fontSize: '1.0625rem',
      fontWeight: 700,
      lineHeight: 1.35,
    },
    subtitle1: { fontSize: '1.03rem', lineHeight: 1.65 },
    subtitle2: { fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.6 },
    body1: { fontSize: '1.03rem', lineHeight: 1.7 },
    body2: { fontSize: '0.875rem', lineHeight: 1.65 },
    caption: {
      fontFamily: marketingFont.mono,
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
    overline: {
      fontFamily: marketingFont.mono,
      fontSize: '0.75rem',
      fontWeight: 500,
      letterSpacing: '0.08em',
    },
    button: {
      fontFamily: marketingFont.display,
      fontSize: '0.8125rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          // Dark-mode Paper otherwise paints an elevation gradient over the
          // flat surfaces the handoff specifies.
          backgroundImage: 'none',
          backgroundColor: marketingColor.surface,
          borderRadius: marketingRadius.md,
          border: `1px solid ${marketingLine.hairline}`,
          boxShadow: 'none',
        },
        outlined: {
          borderColor: marketingLine.hairlineStrong,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: marketingColor.surface,
          borderRadius: marketingRadius.lg,
          border: `1px solid ${marketingLine.hairline}`,
          boxShadow: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: marketingRadius.sm,
          paddingInline: 20,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          backgroundColor: marketingColor.accent,
          color: marketingColor.ink,
          '&:hover': { backgroundColor: marketingColor.accentHover },
        },
        outlined: {
          borderColor: marketingLine.hairlineStrong,
        },
        outlinedPrimary: {
          borderColor: marketingLine.hairlineStrong,
          color: marketingColor.accent,
          '&:hover': {
            borderColor: marketingColor.accent,
            backgroundColor: marketingSurface.accentTint,
          },
        },
        text: {
          '&:hover': { backgroundColor: marketingSurface.accentTint },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: marketingColor.textSecondary,
          '&:hover': {
            color: marketingColor.accent,
            backgroundColor: marketingSurface.accentTint,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: marketingColor.accent,
          textDecorationColor: marketingLine.hairlineStrong,
          '&:hover': { color: marketingColor.accentHover },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: marketingSurface.header,
          backgroundImage: 'none',
          backdropFilter: marketingSurface.headerBlur,
          color: marketingColor.text,
          boxShadow: 'none',
          borderBottom: `1px solid ${marketingLine.hairline}`,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: marketingLine.hairline },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: marketingRadius.sm,
          fontFamily: marketingFont.mono,
          fontWeight: 500,
          letterSpacing: '0.04em',
          backgroundColor: marketingColor.surfaceSelected,
          color: marketingColor.text,
          border: `1px solid ${marketingLine.hairline}`,
        },
        outlined: {
          backgroundColor: 'transparent',
          borderColor: marketingLine.hairlineStrong,
        },
        colorPrimary: {
          backgroundColor: marketingSurface.accentTint,
          color: marketingColor.accent,
          borderColor: marketingLine.hairlineStrong,
        },
        sizeSmall: { height: 24 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: marketingColor.accent },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: marketingFont.mono,
          fontSize: '0.75rem',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: marketingColor.textSecondary,
          '&.Mui-selected': { color: marketingColor.accent },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: marketingColor.surface,
          border: `1px solid ${marketingLine.hairline}`,
          borderRadius: marketingRadius.md,
          boxShadow: 'none',
          '&::before': { display: 'none' },
          '&.Mui-expanded': { borderColor: marketingColor.accent },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: marketingRadius.md,
          border: `1px solid ${marketingLine.hairline}`,
          backgroundColor: marketingColor.surface,
          color: marketingColor.text,
        },
        standardInfo: {
          backgroundColor: marketingSurface.accentTint,
          borderColor: marketingLine.hairlineStrong,
          color: marketingColor.text,
        },
        standardSuccess: {
          backgroundColor: marketingSurface.accentTint,
          borderColor: marketingLine.hairlineStrong,
          color: marketingColor.text,
        },
        standardWarning: {
          backgroundColor: marketingSurface.warningTint,
          borderColor: marketingLine.warning,
          color: marketingColor.text,
        },
        standardError: {
          backgroundColor: marketingSurface.errorTint,
          borderColor: marketingLine.error,
          color: marketingColor.text,
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: marketingRadius.sm,
          backgroundColor: marketingColor.ink,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: marketingLine.hairline,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: marketingLine.hairlineStrong,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: marketingColor.accent,
            borderWidth: 1,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: marketingLine.hairline },
        head: {
          fontFamily: marketingFont.mono,
          fontSize: '0.72rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: marketingColor.textMuted,
          backgroundColor: marketingColor.surfaceSelected,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: marketingColor.surfaceSelected,
          border: `1px solid ${marketingLine.hairline}`,
          fontFamily: marketingFont.mono,
          fontSize: '0.72rem',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: marketingColor.surface,
          border: `1px solid ${marketingLine.hairline}`,
          boxShadow: marketingShadow.chip,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: marketingColor.ink,
          borderRadius: 0,
          borderColor: marketingLine.hairline,
        },
      },
    },
  },
});

export default marketingTheme;
